import React, { useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Switch,
  TextField,
  Button,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Alert,
  Chip,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ReactTimezoneSelect from "react-timezone-select";
import { buildTimezoneSelectStyles } from "./timezoneSelectStyles";

export const DAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABELS = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

export const DEFAULT_BUSINESS_HOURS_SCHEDULE = DAY_KEYS.reduce((acc, day) => {
  acc[day] = { enabled: false, intervals: [] };
  return acc;
}, {});

export const DEFAULT_BUSINESS_HOURS_VALUE = { timezone: "UTC", schedule: DEFAULT_BUSINESS_HOURS_SCHEDULE };

function timeToMinutes(t) {
  if (!t || typeof t !== "string") return null;
  const parts = t.split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1] ?? 0);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/** True if two [start,end] ranges share any minute. Handles overnight ranges (start > end). */
function intervalsOverlap(aStart, aEnd, bStart, bEnd) {
  const as = timeToMinutes(aStart);
  const ae = timeToMinutes(aEnd);
  const bs = timeToMinutes(bStart);
  const be = timeToMinutes(bEnd);
  if (as == null || ae == null || bs == null || be == null) return false;
  const aSegs = as <= ae ? [[as, ae]] : [[as, 1440], [0, ae]];
  const bSegs = bs <= be ? [[bs, be]] : [[bs, 1440], [0, be]];
  return aSegs.some(([a1, a2]) => bSegs.some(([b1, b2]) => Math.max(a1, b1) < Math.min(a2, b2)));
}

/** Validates one day's interval list. Returns an array of human-readable error strings (empty if valid). */
export function validateDayIntervals(intervals) {
  const errors = [];
  if (!intervals || intervals.length === 0) {
    errors.push("At least one time interval is required when the day is enabled.");
    return errors;
  }
  const seen = new Set();
  intervals.forEach((interval, idx) => {
    if (!interval?.start) errors.push(`Interval ${idx + 1}: start time is required.`);
    if (!interval?.end) errors.push(`Interval ${idx + 1}: end time is required.`);
    if (interval?.start && interval?.end) {
      if (interval.start === interval.end) {
        errors.push(`Interval ${idx + 1}: start and end time cannot be the same.`);
      }
      const key = `${interval.start}-${interval.end}`;
      if (seen.has(key)) errors.push(`Interval ${idx + 1}: duplicate of another interval (${interval.start}-${interval.end}).`);
      seen.add(key);
    }
  });
  const complete = intervals.filter((s) => s?.start && s?.end);
  for (let i = 0; i < complete.length; i++) {
    for (let j = i + 1; j < complete.length; j++) {
      if (intervalsOverlap(complete[i].start, complete[i].end, complete[j].start, complete[j].end)) {
        errors.push(`Intervals overlap: ${complete[i].start}-${complete[i].end} and ${complete[j].start}-${complete[j].end}.`);
      }
    }
  }
  return errors;
}

/**
 * Reusable business-hours schedule editor: an IANA timezone selector plus one card per
 * weekday (enable toggle + add/remove/edit time intervals, overnight intervals supported).
 * Controlled component — value is { timezone, schedule } matching
 * businessHoursCondition.service.js's expected condition shape.
 */
const BusinessHoursEditor = ({ value, onChange, expandedDay, onExpandedDayChange }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const timezone = value?.timezone || "UTC";
  const schedule = value?.schedule || DEFAULT_BUSINESS_HOURS_SCHEDULE;

  const dayErrors = useMemo(() => {
    const result = {};
    DAY_KEYS.forEach((day) => {
      const dayConfig = schedule[day];
      result[day] = dayConfig?.enabled ? validateDayIntervals(dayConfig.intervals) : [];
    });
    return result;
  }, [schedule]);

  const emitChange = useCallback((nextTimezone, nextSchedule) => {
    onChange?.({ timezone: nextTimezone, schedule: nextSchedule });
  }, [onChange]);

  const handleTimezoneChange = (tz) => {
    const tzValue = typeof tz === "string" ? tz : tz?.value || "UTC";
    emitChange(tzValue, schedule);
  };

  const handleDayToggle = (day) => {
    const wasEnabled = !!schedule[day]?.enabled;
    const nextDay = wasEnabled
      ? { enabled: false, intervals: [] }
      : { enabled: true, intervals: [{ start: "09:00", end: "17:00" }] };
    emitChange(timezone, { ...schedule, [day]: nextDay });
    if (!wasEnabled) onExpandedDayChange?.(day);
  };

  const handleAddInterval = (day) => {
    const dayConfig = schedule[day] || { enabled: true, intervals: [] };
    const nextIntervals = [...(dayConfig.intervals || []), { start: "", end: "" }];
    emitChange(timezone, { ...schedule, [day]: { ...dayConfig, enabled: true, intervals: nextIntervals } });
  };

  const handleRemoveInterval = (day, idx) => {
    const dayConfig = schedule[day];
    if (!dayConfig) return;
    const nextIntervals = dayConfig.intervals.filter((_, i) => i !== idx);
    emitChange(timezone, { ...schedule, [day]: { ...dayConfig, intervals: nextIntervals } });
  };

  const handleIntervalChange = (day, idx, field, val) => {
    const dayConfig = schedule[day] || { enabled: true, intervals: [] };
    const nextIntervals = [...(dayConfig.intervals || [])];
    nextIntervals[idx] = { ...(nextIntervals[idx] || {}), [field]: val };
    emitChange(timezone, { ...schedule, [day]: { ...dayConfig, intervals: nextIntervals } });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ p: 2, mb: 2, bgcolor: isDark ? "#23272b" : "#fff" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <AccessTimeIcon sx={{ mr: 1, color: "primary.main" }} />
          <Typography variant="subtitle1" fontWeight={600}>Timezone</Typography>
        </Box>
        <ReactTimezoneSelect
          value={timezone}
          onChange={handleTimezoneChange}
          styles={buildTimezoneSelectStyles(isDark)}
        />
      </Paper>

      <Typography variant="subtitle1" fontWeight={600} mb={1}>Weekly schedule</Typography>

      {DAY_KEYS.map((day) => {
        const dayConfig = schedule[day] || { enabled: false, intervals: [] };
        const errors = dayErrors[day];
        return (
          <Accordion
            key={day}
            expanded={expandedDay === day}
            onChange={(e, isExpanded) => onExpandedDayChange?.(isExpanded ? day : null)}
            sx={{ mb: 1, bgcolor: isDark ? "#23272b" : "#fff", "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                <Switch
                  checked={!!dayConfig.enabled}
                  onChange={() => handleDayToggle(day)}
                  onClick={(e) => e.stopPropagation()}
                  size="small"
                />
                <Typography variant="body2" fontWeight={600} sx={{ ml: 2, flex: 1 }}>
                  {DAY_LABELS[day]}
                </Typography>
                {dayConfig.enabled && (dayConfig.intervals || []).length > 0 && (
                  <Chip
                    label={dayConfig.intervals.map((s) => `${s.start || "?"}–${s.end || "?"}`).join(" · ")}
                    size="small"
                    color={errors.length > 0 ? "error" : "default"}
                    sx={{ mr: 1, maxWidth: 260 }}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {dayConfig.enabled ? (
                <Box>
                  {(dayConfig.intervals || []).map((interval, idx) => (
                    <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                      <TextField
                        label="Start"
                        type="time"
                        size="small"
                        value={interval.start || ""}
                        onChange={(e) => handleIntervalChange(day, idx, "start", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="End"
                        type="time"
                        size="small"
                        value={interval.end || ""}
                        onChange={(e) => handleIntervalChange(day, idx, "end", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ flex: 1 }}
                      />
                      <IconButton size="small" color="error" onClick={() => handleRemoveInterval(day, idx)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  <Button startIcon={<AddIcon />} size="small" variant="outlined" onClick={() => handleAddInterval(day)}>
                    Add interval
                  </Button>
                  {errors.length > 0 && (
                    <Alert severity="error" sx={{ mt: 1.5 }}>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {errors.map((err, i) => <li key={i}>{err}</li>)}
                      </ul>
                    </Alert>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Enable this day to add time intervals.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

/** True if every enabled day's intervals pass validateDayIntervals (used to gate saving). */
export function isBusinessHoursValueValid(value) {
  const schedule = value?.schedule || {};
  return DAY_KEYS.every((day) => {
    const dayConfig = schedule[day];
    if (!dayConfig?.enabled) return true;
    return validateDayIntervals(dayConfig.intervals).length === 0;
  });
}

export default BusinessHoursEditor;
